import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateFAQSchema } from '../../utils/seo';

export default function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: '見積もりは無料ですか？',
      answer: 'はい、お見積もりは完全無料です。現地調査を行い、詳細なお見積もりをご提示いたします。お見積もり後のキャンセルも可能ですので、お気軽にご相談ください。'
    },
    {
      question: '工事期間はどのくらいかかりますか？',
      answer: '工事内容により異なりますが、一般的な屋根塗装で3〜5日、葺き替え工事で7〜10日程度です。雨漏り修理は1〜2日で完了することが多いです。詳しくは現地調査時にご説明いたします。'
    },
    {
      question: '雨の日でも工事は可能ですか？',
      answer: '基本的に雨天時は工事を中止いたします。お客様の大切な住まいを守るため、天候を見ながら安全に作業を進めます。工期が延びる場合は事前にご連絡いたします。'
    },
    {
      question: '保証内容について教えてください',
      answer: '工事内容に応じて最長10年の保証をご用意しております。施工不良による雨漏りや不具合が発生した場合、無償で修理対応いたします。保証書を発行し、アフターフォローも万全です。'
    },
    {
      question: '支払い方法は何がありますか？',
      answer: '現金、銀行振込、各種クレジットカードに対応しております。また、リフォームローンのご相談も承っておりますので、お気軽にお問い合わせください。'
    },
    {
      question: '岡山市以外でも対応可能ですか？',
      answer: '岡山市を中心に、近隣地域も対応しております。詳しい対応エリアについては、お電話またはお問い合わせフォームよりご相談ください。'
    }
  ];

  const news = [
    {
      id: 1,
      date: '2024.03.15',
      category: 'お知らせ',
      title: 'ゴールデンウィーク期間中の営業について'
    },
    {
      id: 2,
      date: '2024.03.10',
      category: '施工実績',
      title: '岡山市北区にて屋根葺き替え工事を完了しました'
    },
    {
      id: 3,
      date: '2024.03.05',
      category: 'キャンペーン',
      title: '春の屋根点検キャンペーン実施中'
    },
    {
      id: 4,
      date: '2024.02.28',
      category: 'お知らせ',
      title: '新しいスタッフが加わりました'
    }
  ];

  useSEO({
    title: '岡山市の屋根工事・雨漏り修理 | 10年保証のおかやま屋根工事株式会社',
    description: '岡山市で2,000棟以上の施工実績。雨漏り修理・屋根葺き替え・塗装まで自社職人が対応。無料見積もり実施中。最長10年保証で安心。岡山市北区を中心に地域密着で確かな技術と適正価格をご提供します。',
    keywords: '岡山市,屋根工事,雨漏り修理,屋根葺き替え,屋根塗装,防水工事,岡山市北区,自社職人,10年保証',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema(),
        generateFAQSchema(faqs)
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/dc1fcdfc59cd54a9c8b060e9ea691e3b.jpeg"
            alt="岡山市 屋根工事 職人 安心施工"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/20"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl ml-auto text-right">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
              屋根のプロフェッショナル<br />
              確かな技術でお応えします
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 leading-relaxed">
              地域密着・自社職人施工だから、<br />
              安心の品質と適正価格を実現
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-end">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-white bg-[#ff6b35] rounded-lg hover:bg-[#e55a28] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                今すぐ無料見積もり
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </a>
              <Link
                to="/case-studies"
                className="inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                施工事例を見る
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-sm sm:text-base text-[#1e3a5f] font-semibold mb-3 tracking-wider uppercase">選ばれる理由</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              20年以上、岡山の屋根を守り続けています
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-[#1e3a5f]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">施工実績2,000棟以上</h3>
              <p className="text-gray-600 leading-relaxed">
                岡山市を中心に、10年以上で2,000棟以上の屋根工事を手がけてきました。豊富な経験と確かな技術で、どんな屋根のお悩みも解決します。
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-[#1e3a5f]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">自社職人による直接施工</h3>
              <p className="text-gray-600 leading-relaxed">
                下請けに丸投げせず、経験豊富な自社職人が責任を持って施工。中間マージンがないため、高品質な工事を適正価格でご提供できます。
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-3xl text-[#1e3a5f]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">最長10年保証</h3>
              <p className="text-gray-600 leading-relaxed">
                施工後も安心していただけるよう、最長10年の保証をご用意。万が一のトラブルにも迅速に対応し、お客様の大切な住まいを長期間守ります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">お知らせ</h2>
          </div>
          
          <div className="space-y-4">
            {news.map((item) => (
              <div
                key={item.id}
                className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-300 cursor-pointer"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <span className="text-sm font-semibold text-gray-500">{item.date}</span>
                  <span className="inline-block px-3 py-1 bg-[#1e3a5f] text-white text-xs font-semibold rounded-full w-fit">
                    {item.category}
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 flex-1">{item.title}</h3>
                  <i className="ri-arrow-right-line text-xl text-[#1e3a5f] hidden sm:block"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Introduction */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-2 bg-[#1e3a5f] text-white text-sm font-semibold rounded-full mb-6">
                サービス
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                屋根のお悩み、<br />
                すべて解決します
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                屋根工事一筋20年以上、 現場仕事のみを続けてきました。当方は工事を売るための無料点検は行っておりません。訪問販売や他社見積で「本当に必要な工事か分からない」と悩んだ時の、 屋根のセカンドオピニオンとして、 第三者目線で正直に確認します。無理な営業・契約は一切ありません。小さな修理で済む場合は、 その場で対応も可能です。
              </p>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-white bg-[#1e3a5f] rounded-lg hover:bg-[#152a45] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                サービス詳細へ
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </Link>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="w-full h-[400px] sm:h-[500px] lg:h-[600px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/ff1fd15bae49f80f96e406d1504ee681.jpg"
                  alt="岡山市 屋根工事 職人 プロフェッショナル"
                  className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">施工事例</h2>
            <p className="text-base sm:text-lg text-gray-600">2,000棟以上の実績から厳選した施工事例をご紹介</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Beautiful%20traditional%20Japanese%20tile%20roof%20after%20renovation%20in%20Okayama%2C%20clear%20blue%20sky%20background%2C%20professional%20architectural%20photography%20without%20any%20text%20or%20signage%2C%20showing%20quality%20craftsmanship%20and%20attention%20to%20detail&width=600&height=800&seq=case001&orientation=portrait"
                alt="岡山市北区 K様邸 瓦屋根葺き替え工事"
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">岡山市北区 K様邸</h3>
                <p className="text-sm sm:text-base text-white/90">瓦屋根葺き替え工事</p>
              </div>
            </div>
            
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20roofing%20workers%20carefully%20installing%20new%20roof%20tiles%20on%20residential%20house%20in%20Okayama%2C%20professional%20work%20in%20progress%20scene%20without%20any%20text%20or%20signage%2C%20showing%20skilled%20craftsmanship%20and%20safety%20measures&width=600&height=800&seq=case002&orientation=portrait"
                alt="岡山市中区 T様邸 雨漏り修理工事"
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">岡山市中区 T様邸</h3>
                <p className="text-sm sm:text-base text-white/90">雨漏り修理工事</p>
              </div>
            </div>
            
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Freshly%20painted%20modern%20Japanese%20residential%20roof%20with%20beautiful%20finish%20in%20Okayama%2C%20architectural%20detail%20photography%20without%20any%20text%20or%20signage%2C%20showing%20professional%20quality%20and%20durability&width=600&height=800&seq=case003&orientation=portrait"
                alt="岡山市南区 S様邸 屋根塗装工事"
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">岡山市南区 S様邸</h3>
                <p className="text-sm sm:text-base text-white/90">屋根塗装工事</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-10 sm:mt-12">
            <Link
              to="/case-studies"
              className="inline-flex items-center text-[#1e3a5f] font-semibold hover:underline cursor-pointer text-base sm:text-lg"
            >
              もっと見る
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-star-fill text-[#1e3a5f] text-xl"></i>
              <span className="text-sm sm:text-base text-[#1e3a5f] font-semibold tracking-wider uppercase">お客様の声</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f]">お客様の声</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20middle-aged%20Japanese%20man%20homeowner%20smiling%20confidently%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review001&orientation=squarish"
                    alt="田中様"
                    className="w-full h-full object-cover object-top rounded-full"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg">田中様</h4>
                  <p className="text-sm text-gray-600">岡山市中区在住</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-3xl text-[#1e3a5f]/20 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                雨漏りで困っていましたが、迅速に対応していただき本当に助かりました。職人さんの説明も丁寧で、安心してお任せできました。
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/abe852ef421be38fa3dce1ea65a3c7bc.jpeg"
                    alt="佐藤様"
                    className="w-full h-full object-cover object-top rounded-full"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg">佐藤様</h4>
                  <p className="text-sm text-gray-600">岡山市北区在住</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-3xl text-[#1e3a5f]/20 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                見積もりが明確で、追加費用も一切ありませんでした。仕上がりも美しく、10年保証も付いているので安心です。
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elderly%20Japanese%20man%20homeowner%20smiling%20kindly%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review003&orientation=squarish"
                    alt="山田様"
                    className="w-full h-full object-cover object-top rounded-full"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg">山田様</h4>
                  <p className="text-sm text-gray-600">岡山市南区在住</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-3xl text-[#1e3a5f]/20 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                地元の会社で安心感がありました。工事中も近隣への配慮が行き届いており、プロフェッショナルな対応に感謝しています。
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Japanese%20woman%20homeowner%20smiling%20happily%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review004&orientation=squarish"
                    alt="鈴木様"
                    className="w-full h-full object-cover object-top rounded-full"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg">鈴木様</h4>
                  <p className="text-sm text-gray-600">岡山市東区在住</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-3xl text-[#1e3a5f]/20 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                初めての屋根工事で不安でしたが、丁寧な説明と確かな技術で安心してお願いできました。アフターフォローも万全です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">よくある質問</h2>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            <FAQItem
              question="見積もりは無料ですか？"
              answer="はい、お見積もりは完全無料です。現地調査を行い、詳細なお見積もりをご提示いたします。お見積もり後のキャンセルも可能ですので、お気軽にご相談ください。"
            />
            <FAQItem
              question="工事期間はどのくらいかかりますか？"
              answer="工事内容により異なりますが、一般的な屋根塗装で3〜5日、葺き替え工事で7〜10日程度です。雨漏り修理は1〜2日で完了することが多いです。詳しくは現地調査時にご説明いたします。"
            />
            <FAQItem
              question="雨の日でも工事は可能ですか？"
              answer="基本的に雨天時は工事を中止いたします。お客様の大切な住まいを守るため、天候を見ながら安全に作業を進めます。工期が延びる場合は事前にご連絡いたします。"
            />
            <FAQItem
              question="保証内容について教えてください"
              answer="工事内容に応じて最長10年の保証をご用意しております。施工不良による雨漏りや不具合が発生した場合、無償で修理対応いたします。保証書を発行し、アフターフォローも万全です。"
            />
            <FAQItem
              question="支払い方法は何がありますか？"
              answer="現金、銀行振込、各種クレジットカードに対応しております。また、リフォームローンのご相談も承っておりますので、お気軽にお問い合わせください。"
            />
            <FAQItem
              question="岡山市以外でも対応可能ですか？"
              answer="岡山市を中心に、近隣地域も対応しております。詳しい対応エリアについては、お電話またはお問い合わせフォームよりご相談ください。"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Okayama%20city%20skyline%20with%20clear%20blue%20sky%20and%20traditional%20Japanese%20architecture%2C%20peaceful%20urban%20landscape%20without%20any%20text%20or%20signage%2C%20professional%20cityscape%20photography&width=1920&height=800&seq=cta001&orientation=landscape"
            alt="岡山市 街並み"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 sm:mb-8">
            無料見積もり・ご相談<br />
            今すぐお電話ください
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-white mb-8 sm:mb-12 leading-relaxed">
            営業時間 9:00〜18:00<br />
            お気軽にお問い合わせください
          </p>
          
          <a
            href="tel:0860123456"
            className="inline-flex items-center justify-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#ff6b35] rounded-2xl hover:bg-[#e55a28] transition-all duration-300 shadow-2xl hover:shadow-3xl cursor-pointer mb-6"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-phone-fill text-2xl sm:text-3xl text-white"></i>
            </div>
            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white whitespace-nowrap">086-012-3456</span>
            <i className="ri-arrow-right-up-line text-2xl sm:text-3xl text-white"></i>
          </a>
          
          <p className="text-sm sm:text-base text-white/80">または</p>
          
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 mt-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            お問い合わせフォームへ
            <i className="ri-mail-line ml-2 text-xl"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
      >
        <span className="font-bold text-gray-900 pr-4 text-base sm:text-lg">{question}</span>
        <i className={`ri-arrow-down-s-line text-2xl text-[#1e3a5f] transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && (
        <div className="px-6 sm:px-8 pb-5 sm:pb-6">
          <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{answer}</p>
        </div>
      )}
    </div>
  );
}
